import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Clock, Mail, MessageSquare, Loader2, ExternalLink, Reply, Check } from 'lucide-react';
import { toast } from 'sonner';
import { API, useAuth } from '../App';
import { devError } from '../utils/logger';
import ReplyDialog from './ReplyDialog';

const formatRelative = (iso) => {
  if (!iso) return '';
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.round(diffMs / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    return `${days}d ago`;
  } catch {
    return iso;
  }
};

/**
 * Build a pre-filled mailto: link with a friendly availability-confirmation
 * template. Suppliers can hit "Reply" once and the body of the email is
 * already drafted — most enquiry conversions hinge on speed of first reply.
 */
const buildReplyMailto = ({ to, listingTitle, projectName, supplierName }) => {
  if (!to) return null;
  const subject = `Re: ${listingTitle} — still available`;
  const greeting = projectName
    ? `Hi there,\n\nThanks for the enquiry about "${listingTitle}" for the ${projectName} project.`
    : `Hi there,\n\nThanks for the enquiry about "${listingTitle}".`;
  const body = [
    greeting,
    "",
    "Yes — it's still available. A few options for next steps:",
    "  • Pop by the yard for a look (let us know which day suits)",
    "  • I can email more photos / measurements if helpful",
    "  • Happy to put it aside for 48h if you're keen",
    "",
    "Just hit reply and let me know which works best.",
    "",
    "Cheers,",
    supplierName || "PatinaMart Supplier",
  ].join("\n");
  return `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

export default function PendingEnquiriesPanel() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [enquiries, setEnquiries] = useState([]);
  const [marking, setMarking] = useState({});
  const [replyOpen, setReplyOpen] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/supplier/pending-enquiries?limit=20`);
        if (!cancelled) setEnquiries(data.enquiries || []);
      } catch (e) {
        if (cancelled) return;
        devError('pending-enquiries load failed', e);
        toast.error('Could not load pending enquiries');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const markReplied = async (q) => {
    if (!q.inquiry_id) {
      toast.info("This older enquiry can't be tracked — reply directly via email.");
      return;
    }
    setMarking((m) => ({ ...m, [q.event_id]: true }));
    try {
      await axios.post(`${API}/supplier/inquiries/${q.inquiry_id}/mark-replied`);
      setEnquiries((list) => list.filter((x) => x.event_id !== q.event_id));
      toast.success('Marked as replied');
    } catch (e) {
      devError('mark-replied failed', e);
      toast.error("Couldn't mark as replied");
    } finally {
      setMarking((m) => {
        const { [q.event_id]: _, ...rest } = m;
        return rest;
      });
    }
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-border/20 bg-card/30 p-6" data-testid="pending-enquiries-loading">
        <Loader2 className="w-5 h-5 animate-spin text-accent mx-auto" />
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border/20 bg-card/30 p-6" data-testid="pending-enquiries-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-accent" />
          <h3 className="text-sm uppercase tracking-widest text-muted-foreground">Pending enquiries</h3>
        </div>
        <span className="text-xs text-muted-foreground/70" title="At 24h we'll nudge the supplier; at 40h the PatinaMart team is alerted to step in.">
          Reply within 24h
        </span>
      </div>

      {enquiries.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center" data-testid="pending-enquiries-empty">
          No enquiries yet. They'll appear here when a designer asks about a listing inside Revit, ArchiCAD or SketchUp.
        </p>
      ) : (
        <ul className="divide-y divide-border/15" data-testid="pending-enquiries-list">
          {enquiries.map((q) => {
            const mailto = buildReplyMailto({
              to: q.user_email,
              listingTitle: q.listing_title,
              projectName: q.project_name,
              supplierName: user?.company_name || user?.name,
            });
            return (
              <li
                key={q.event_id}
                className="py-3 flex items-start justify-between gap-3"
                data-testid={`pending-enquiry-${q.event_id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground truncate">
                    <Link to={`/listing/${q.listing_id}`} className="hover:text-accent">
                      {q.listing_title}
                    </Link>
                  </p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {formatRelative(q.created_at)}
                    </span>
                    {q.plugin && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-secondary/40 text-[10px] uppercase tracking-wider">
                        {q.plugin}
                      </span>
                    )}
                    {q.project_name && (
                      <span className="text-muted-foreground/80 truncate">project: {q.project_name}</span>
                    )}
                  </div>
                  {q.user_email && (
                    <div className="mt-1 flex items-center gap-1 text-xs text-accent/80">
                      <Mail className="w-3 h-3" />
                      <a
                        href={`mailto:${q.user_email}`}
                        className="hover:underline"
                        data-testid={`pending-enquiry-email-${q.event_id}`}
                      >
                        {q.user_email}
                      </a>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => setReplyOpen(q)}
                    disabled={!q.inquiry_id}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md bg-accent/10 border border-accent/30 text-accent hover:bg-accent/20 transition-colors disabled:opacity-50"
                    data-testid={`pending-enquiry-reply-${q.event_id}`}
                    title={q.inquiry_id ? 'Reply via PatinaMart (sends real email + clears the 24h escalation)' : 'Older enquiry — reply via your email client'}
                  >
                    <Reply className="w-3 h-3" />
                    Reply
                  </button>
                  <button
                    type="button"
                    onClick={() => markReplied(q)}
                    disabled={!q.inquiry_id || !!marking[q.event_id]}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid={`pending-enquiry-mark-replied-${q.event_id}`}
                    title={q.inquiry_id ? 'Mark as replied (clears the 24h escalation)' : 'Older enquiry — track via email only'}
                  >
                    {marking[q.event_id] ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Check className="w-3 h-3" />
                    )}
                    Mark replied
                  </button>
                  <Link
                    to={`/listing/${q.listing_id}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-muted-foreground hover:text-accent"
                    aria-label="Open listing"
                    data-testid={`pending-enquiry-open-${q.event_id}`}
                  >
                    <ExternalLink className="w-3 h-3" />
                    Open
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <ReplyDialog
        open={!!replyOpen}
        inquiry={replyOpen}
        onClose={() => setReplyOpen(null)}
        onReplied={() => setEnquiries((arr) => arr.filter((x) => x.event_id !== replyOpen?.event_id))}
      />
    </div>
  );
}
